import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaDownload, 
  FaSearch, 
  FaBook, 
  FaQuestionCircle, 
  FaFileAlt,
  FaArrowRight,
  FaTimes,
  FaBuilding,
  FaCloud,
  FaMobileAlt,
  FaUsers,
  FaSchool,
  FaGlobeAfrica,
  FaRocket,
  FaLightbulb,
  FaShieldAlt,
  FaLink
} from 'react-icons/fa';

// ─── Data ─────────────────────────────────────────────

// Product Resources - Exactly what we have
const productResources = [
  { 
    title: 'PowerFinancials', 
    description: 'Core banking and SACCO management system for financial institutions.',
    icon: FaBuilding,
    color: 'from-blue-500 to-indigo-600',
    link: '/products/power-financials'
  },
  { 
    title: 'Microsoft Dynamics 365 / Business Central', 
    description: 'Enterprise resource planning solution from Microsoft for comprehensive business management.',
    icon: FaCloud,
    color: 'from-indigo-500 to-blue-600',
    link: '/products/dynamics-365'
  },
  { 
    title: 'Payment Integration Platform', 
    description: 'Secure integration with MPESA, Bank APIs, ISO8583, ATM, POS, and Mobile Money.',
    icon: FaMobileAlt,
    color: 'from-emerald-500 to-teal-600',
    link: '/products/payment-integration'
  },
  { 
    title: 'HR & Payroll', 
    description: 'Complete HR management including payroll, leave, claims, tax, and performance tracking.',
    icon: FaUsers,
    color: 'from-purple-500 to-pink-600',
    link: '/products/hr-payroll'
  },
  { 
    title: 'School Management', 
    description: 'Complete school management system for admissions, examinations, fee processing, and parent communication.',
    icon: FaSchool,
    color: 'from-sky-500 to-blue-600',
    link: '/products/school-management'
  },
];

// Insights - Exactly what we have from the content pack
const insights = [
  { 
    title: 'What Should You Look for When Replacing a SACCO Core System?',
    category: 'Guide',
    excerpt: 'A practical guide covering functionality, migration, integrations, security, configurability, support, reporting and the ability to evolve with the SACCO.',
    readTime: '6 min read',
    icon: FaBook
  },
  { 
    title: 'Why Loan Management Is More Than Disbursement and Repayment',
    category: 'Insight',
    excerpt: 'A practical look at appraisal, schedules, charges, top-ups, restructuring, collateral, guarantors, repayments, reconciliation and accounting.',
    readTime: '5 min read',
    icon: FaLightbulb
  },
  { 
    title: 'Automating Payment Reconciliation for SACCOs and MFIs',
    category: 'Guide',
    excerpt: 'How bank and mobile-money integrations can reduce manual matching, improve visibility and speed up posting.',
    readTime: '4 min read',
    icon: FaFileAlt
  },
  { 
    title: 'Bank Integration: APIs, Notifications and Transaction Reconciliation',
    category: 'Technical',
    excerpt: 'A practical explanation of how financial institutions can connect core systems to banks and turn transaction feeds into useful business workflows.',
    readTime: '7 min read',
    icon: FaLink
  },
  { 
    title: 'ISO 8583: An Established Standard That Still Matters in Financial Transaction Processing',
    category: 'Technical',
    excerpt: 'A plain-English explanation of ISO 8583 and where established financial transaction messaging still fits alongside modern APIs.',
    readTime: '5 min read',
    icon: FaShieldAlt
  },
];

// FAQs - Exactly what we have from the content pack
const faqs = [
  { 
    question: 'Can PowerFinancials handle loan top-ups, restructuring and rescheduling?',
    answer: 'Yes. PowerFinancials can support configurable lending workflows, including loan products, charges and evolving repayment requirements.'
  },
  { 
    question: 'Can PowerFinancials integrate with M-PESA, banks and CRB services?',
    answer: 'Yes. Micropoint has experience integrating financial systems with banks, M-PESA and other payment channels.'
  },
  { 
    question: 'Can existing member and loan data be migrated?',
    answer: 'Yes. Data migration is part of Micropoint\'s implementation experience, including member, financial and operational data.'
  },
  { 
    question: 'Can loan products, charges and repayment rules be configured?',
    answer: 'Yes. The platform can be configured around different products, charges, repayment rules and operational requirements.'
  },
  { 
    question: 'Can Business Central be customized and extended around our processes?',
    answer: 'Yes. Micropoint provides Microsoft Dynamics 365 implementation, customization, integration and extension services.'
  },
  { 
    question: 'Can Micropoint integrate our system with a bank?',
    answer: 'Yes. Micropoint has experience with bank transaction notifications, payment integrations, cheque processing and related financial workflows.'
  },
  { 
    question: 'Can payment collections be automatically matched and reconciled?',
    answer: 'Yes. Payment integrations can be designed to match incoming transactions to the relevant member or customer and route exceptions for review.'
  },
];

// Featured Case Studies - The 6 flagship stories as specified
const featuredCaseStudies = [
  { 
    title: 'From Core Finance to a Connected Digital Business',
    client: 'Springboard Capital',
    description: 'A connected Dynamics 365 environment bringing credit, finance, procurement, HR, payroll, ESS, CRM and mobile lending together.',
    category: 'Enterprise Transformation',
    gradient: 'from-blue-600 to-indigo-700',
    icon: FaCloud,
    link: '/case-studies/springboard-capital'
  },
  { 
    title: 'From One Generation of PowerFinancials to the Next',
    client: 'Dream Credit',
    description: 'A long-standing PowerFinancials relationship now entering its next chapter with a next-generation platform upgrade.',
    category: 'Growth Stories',
    gradient: 'from-blue-500 to-indigo-600',
    icon: FaRocket,
    link: '/case-studies/dream-credit'
  },
  { 
    title: 'From a Small Office in Kiambu to a Growing Regulated SACCO',
    client: 'DIMKES SACCO',
    description: 'A small Kiambu office, PowerFinancials, rapid growth and a journey that eventually led to Microsoft Dynamics 365 Business Central.',
    category: 'Growth Stories',
    gradient: 'from-emerald-500 to-teal-600',
    icon: FaRocket,
    link: '/case-studies/dimkes-sacco'
  },
  { 
    title: 'From One Employee to a Growing Credit Business',
    client: 'MyCredit',
    description: 'A one-employee startup, PowerFinancials and a growth journey that eventually took MyCredit into a much larger operating environment.',
    category: 'Growth Stories',
    gradient: 'from-purple-500 to-indigo-600',
    icon: FaRocket,
    link: '/case-studies/mycredit'
  },
  { 
    title: 'When Security Couldn\'t Be Compromised: The Mkopo Radi Story',
    client: 'Bands SACCO',
    description: 'After years of searching for a provider capable of working within demanding security requirements, Bands SACCO found a technology partner in Micropoint.',
    category: 'Digital & Integration',
    gradient: 'from-amber-500 to-orange-600',
    icon: FaMobileAlt,
    link: '/case-studies/bands-sacco'
  },
  { 
    title: 'Tailoring Financial Technology for a Mogadishu Institution',
    client: 'KOBCIYE LLC',
    description: 'PowerFinancials tailored for a Mogadishu-based microfinance institution, demonstrating regional reach and customization.',
    category: 'Financial Inclusion',
    gradient: 'from-yellow-500 to-amber-600',
    icon: FaGlobeAfrica,
    link: '/case-studies/kobciye'
  },
];

// ─── Component ────────────────────────────────────────
export default function Resources() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('insights');

  const filteredInsights = insights.filter(insight =>
    insight.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    insight.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProducts = productResources.filter(product =>
    product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCaseStudies = featuredCaseStudies.filter(study =>
    study.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    study.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    study.client.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <section className="py-16 md:py-20 bg-white min-h-screen">
      <div className="container mx-auto px-4 max-w-6xl">
        
        {/* ─── Hero Section ─── */}
        <div className="text-center mb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-blue-100">
              Resources & Insights
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-slate-900 tracking-tight">
              Resources <span className="font-semibold text-blue-600">&amp; Insights</span>
            </h1>
            <div className="w-16 h-1 bg-blue-600 mx-auto mt-4 rounded-full" />
            <p className="text-slate-500 mt-4 max-w-xl mx-auto text-sm">
              Practical guidance, product information and real-world experience from Micropoint Systems.
            </p>
          </motion.div>
        </div>

        {/* ─── Search ─── */}
        <div className="mb-10 max-w-2xl mx-auto">
          <div className="relative">
            <input
              type="text"
              placeholder="Search resources, articles, FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pl-12 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all bg-white text-sm placeholder:text-slate-400 shadow-sm"
            />
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <FaTimes className="text-xs" />
              </button>
            )}
          </div>
        </div>

        {/* ─── Tab Navigation ─── */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {[
            { id: 'insights', label: '📖 Insights & Articles' },
            { id: 'products', label: '📦 Product Resources' },
            { id: 'case-studies', label: '📋 Featured Case Studies' },
            { id: 'faqs', label: '❓ FAQs' },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                    : 'bg-white text-slate-600 hover:text-blue-600 hover:bg-slate-50 border border-slate-200 hover:border-blue-300'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ─── Tab Content ─── */}

        {/* ─── INSIGHTS & ARTICLES ─── */}
        {activeTab === 'insights' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-blue-600 rounded-full" />
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Insights & Articles</h2>
              <span className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full font-medium">
                {filteredInsights.length}
              </span>
            </div>

            {filteredInsights.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-4xl mb-3">🔍</div>
                <p className="text-slate-500">No insights found matching your search.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredInsights.map((insight, index) => {
                  const Icon = insight.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="bg-white rounded-xl p-5 border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all group cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-all">
                          <Icon className="text-blue-500 text-lg" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="inline-block bg-blue-50 text-blue-700 text-[10px] px-2.5 py-0.5 rounded-full font-medium">
                              {insight.category}
                            </span>
                            <span className="text-xs text-slate-400">{insight.readTime}</span>
                          </div>
                          <h4 className="font-bold text-slate-900 text-sm mb-1 group-hover:text-blue-600 transition-colors">
                            {insight.title}
                          </h4>
                          <p className="text-sm text-slate-500 line-clamp-2">{insight.excerpt}</p>
                          <button className="mt-3 text-blue-600 font-semibold text-sm hover:text-blue-700 transition-colors flex items-center gap-1 group-hover:gap-2">
                            Read More <FaArrowRight className="text-xs" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── PRODUCT RESOURCES ─── */}
        {activeTab === 'products' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-blue-600 rounded-full" />
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Product Resources</h2>
              <span className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full font-medium">
                {filteredProducts.length}
              </span>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-4xl mb-3">🔍</div>
                <p className="text-slate-500">No products found matching your search.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredProducts.map((resource, index) => {
                  const Icon = resource.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="bg-white rounded-xl p-5 border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all group"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${resource.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-all shadow-sm`}>
                          <Icon className="text-xl text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-slate-900 text-base mb-1 group-hover:text-blue-600 transition-colors">
                            {resource.title}
                          </h4>
                          <p className="text-sm text-slate-500">{resource.description}</p>
                          <Link 
                            to={resource.link}
                            className="inline-flex items-center gap-1 text-blue-600 font-semibold text-sm hover:text-blue-700 transition-colors mt-2 group-hover:gap-2"
                          >
                            Learn More <FaArrowRight className="text-xs" />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── FEATURED CASE STUDIES ─── */}
        {activeTab === 'case-studies' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-blue-600 rounded-full" />
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Featured Case Studies</h2>
              <span className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full font-medium">
                Real businesses. Real growth. Real technology.
              </span>
            </div>

            {filteredCaseStudies.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-4xl mb-3">🔍</div>
                <p className="text-slate-500">No case studies found matching your search.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredCaseStudies.map((study, index) => {
                  const Icon = study.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="bg-white rounded-xl border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all group overflow-hidden"
                    >
                      <div className={`h-24 bg-gradient-to-br ${study.gradient} flex items-center justify-center relative overflow-hidden`}>
                        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="white" fill-opacity="0.1"%3E%3Cpath d="M30 0L0 30l30 30 30-30z"/%3E%3C/g%3E%3C/svg%3E")' }} />
                        <Icon className="text-3xl text-white drop-shadow-lg relative z-10" />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="inline-block bg-blue-50 text-blue-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
                            {study.category}
                          </span>
                        </div>
                        <h4 className="font-bold text-slate-900 text-sm mb-0.5 group-hover:text-blue-600 transition-colors line-clamp-1">
                          {study.title}
                        </h4>
                        <p className="text-xs text-blue-600 font-medium mb-1">{study.client}</p>
                        <p className="text-xs text-slate-500 line-clamp-2">{study.description}</p>
                        <Link 
                          to={study.link}
                          className="inline-flex items-center gap-1 text-blue-600 font-semibold text-xs hover:text-blue-700 transition-colors mt-2 group-hover:gap-2"
                        >
                          Read Full Story <FaArrowRight className="text-[10px]" />
                        </Link>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── FAQS ─── */}
        {activeTab === 'faqs' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-blue-600 rounded-full" />
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Frequently Asked Questions</h2>
              <span className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full font-medium">
                {filteredFaqs.length}
              </span>
            </div>

            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-4xl mb-3">🔍</div>
                <p className="text-slate-500">No FAQs found matching your search.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFaqs.map((faq, index) => {
                  const [isOpen, setIsOpen] = useState(false);
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="bg-white rounded-xl border border-slate-100 hover:border-blue-200 transition-all overflow-hidden"
                    >
                      <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="w-full px-5 py-4 text-left flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors"
                      >
                        <span className="text-sm font-medium text-slate-800">{faq.question}</span>
                        <span className={`text-blue-600 text-sm transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </button>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="px-5 pb-4"
                        >
                          <div className="pt-3 border-t border-slate-100">
                            <p className="text-sm text-slate-600">{faq.answer}</p>
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── Bottom CTA ─── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative overflow-hidden rounded-2xl p-8 md:p-10 text-center text-white mt-12"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.06)_0%,_transparent_70%)]" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 border border-white/10">
              <FaQuestionCircle className="text-xl text-blue-400" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold mb-2 text-white">
              Need help choosing the <span className="text-blue-400">right solution</span>?
            </h3>
            <p className="text-white/50 text-sm mb-6 max-w-lg mx-auto">
              Tell us what you are trying to achieve and our team will help you identify the right Micropoint solution.
            </p>
            <Link 
              to="/contact" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full font-semibold text-sm transition-all hover:shadow-lg hover:shadow-blue-600/30 hover:-translate-y-0.5"
            >
              Talk to Our Team
            </Link>
          </div>
        </motion.div>

      </div>
    </section>
  );
}